import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box as Wrapper, Button, Icon, Text, Space, Stack } from '@tonic-ui/react';
import { CognitoUser } from 'amazon-cognito-identity-js';

import { AuthContext } from '../../context/AuthContext';
import { FormContainer, InputField, mfaResetInput, SubmitButton } from '../../components/forms';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import { gotoSignIn } from '../../utils/helpers';
import RouteLink from '../../components/route-link';
import {
  isUserPoolProxyAPIError,
  filterUserPoolProxyMessage,
  UserPoolProxyAPIError,
} from '../../services/user-pool-proxy-api';
import { UserPoolProxyAPI } from '../../services';

const MfaReset = (): JSX.Element => {
  const [apiErrors, setApiErrors] = useState({ error: '' });
  const { t } = useTranslation();
  const [hasSubmittedResetCode, setHasSubmittedResetCode] = useState(false);
  const { cognitoUser, eventDistributor } = useContext(AuthContext);
  const focusRef = useRef<HTMLInputElement | null>(null);
  const deleteMFA = UserPoolProxyAPI.useDeleteMFA();

  useEffect(() => {
    if (Object.keys(cognitoUser).length === 0) {
      // use-case: user circumvents login process by navigating to /mfa-reset manually
      gotoSignIn(eventDistributor);
    }
  }, []);

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current = focusRef.current?.focus();
    }
  }, []);

  const handleSubmit = async ({ setSubmitting, resetForm, resetCode }) => {
    setApiErrors({ error: '' });

    try {
      await deleteMFA.mutateAsync({ cognitoUser: cognitoUser as CognitoUser, resetCode });
      setHasSubmittedResetCode(true);
    } catch (error: unknown) {
      const errMsg: string | unknown = (error as Error | UserPoolProxyAPIError | undefined)?.message
        ? (error as Error | UserPoolProxyAPIError).message
        : (error as unknown);
      console.error('Error: ', errMsg);
      if (isUserPoolProxyAPIError(error)) {
        // Check if session expired by message. No way to continue except by user reentering login
        const strAuthInvalid = 'Your authentication session is not valid';
        if (error.status === 400 && error.message.includes(strAuthInvalid)) {
          return eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: '/' });
        }
        setApiErrors({ error: filterUserPoolProxyMessage(error.message) });
      } else {
        setApiErrors({ error: typeof errMsg === 'string' ? errMsg : '' });
      }
      setSubmitting(false);
      resetForm();
    }
  };

  const defaultValues = {
    resetCode: '',
  };

  return (
    <Wrapper width={360} margin="128px auto">
      {apiErrors?.error && (
        <NotificationBanner
          name="mfa-reset-form"
          severity="error"
          shouldOpen={true}
          bannerText={apiErrors.error}
        />
      )}
      <FormContainer
        id="mfa-reset-form"
        customValidation={mfaResetInput}
        initialValues={defaultValues}
        submitAction={handleSubmit}
      >
        <Stack marginTop="8x">
          <Text size="2xl" paddingBottom="8x">
            {t('mfa.title')}
          </Text>
          {!hasSubmittedResetCode && (
            <>
              <Text size="xl" paddingBottom="4x">
                {t('mfaReset.reset')}
              </Text>
              <Text paddingBottom="8x">{t('mfaReset.description')}</Text>
              <InputField
                ref={focusRef}
                id="reset-code"
                type="text"
                label={t('mfaReset.resetCodeLabel')}
                name="resetCode"
                isRequired={true}
                maxLength={36}
              />
              <Space height="12x" />
              <SubmitButton
                label={t('mfaReset.reset')}
                id="mfa-reset-submit-btn"
                isLoading={deleteMFA.isLoading}
              />
              <Wrapper textAlign="center" marginTop="8x">
                <RouteLink href="/mfa-login">{t('goBack')}</RouteLink>
              </Wrapper>
            </>
          )}
          {hasSubmittedResetCode && (
            <>
              <Stack direction="row" spacing="4x" paddingBottom="4x">
                <Icon icon="check-bold" size="6x" color="green:50" />
                <Text>{t('mfaReset.resetSuccess')}</Text>
              </Stack>
              <Text>{t('mfaReset.continue')}</Text>
              <Button
                variant="primary"
                marginTop="12x"
                onClick={() => {
                  eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'login' });
                }}
              >
                {t('continue')}
              </Button>
            </>
          )}
        </Stack>
      </FormContainer>
    </Wrapper>
  );
};

MfaReset.displayName = 'MfaReset';

export default MfaReset;
